/* eslint-disable max-lines */
import { useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import { getKioskAuthorization, kioskPaymentStop } from 'api/Kiosk';

import useError from 'lib/hooks/useError';
import { inRn, mString, snakeToTitleCase } from 'lib/helpers/utility';
import { socket, socketEmit, socketOff, socketOn } from 'lib/clients/socket';
import { PAID_TRANSACTION_STATUSES } from 'lib/constants/transaction/completedTransactionStatuses';

import Modal from 'components/shared/Modal/Modal';
import Loader from 'components/shared/Loader/Loader';
import { showNotification } from 'components/shared/Notification/Notification';
import ErrorPaymentModal from 'components/Payments/NewPayment/PaymentContent/components/ErrorModal';
import { usePaymentContext } from 'components/Payments/NewPayment/PaymentContent/context/PaymentContext';

import Footer from './components/Footer';
import Header from './components/Header';
import CardContent from './components/CardContent';
import Information from './components/Information';
import { classifyReaderStatus } from './lib/readerStatus';
import { toDisplayPaymentMessage } from './lib/paymentMessages';

const RECONCILE_AFTER_MS = 60000;
// A payment pushed from the portal arms the reader before the patient has reached the kiosk, so
// the walk-over has to fit inside the window that cancels the sale.
const AUTO_STARTED_RECONCILE_AFTER_MS = 120000;

const PayFromKioskModal = ({
  isOpen,
  handleClose,
  patientId,
  onSuccess,
  token,
  autoStarted = false
}) => {
  const [paymentKind, setPaymentKind] = useState('saved-cards');
  const [cardMessage, setCardMessage] = useState('');
  // Reader phase: initiating | ready | authorizing | success | canceled | error.
  const [status, setStatus] = useState('initiating');
  const [isClosing, setIsClosing] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);

  const { values, terminalData } = usePaymentContext();

  const scrollRef = useRef(null);
  const { error, setErrorMessage } = useError();
  const navigate = useNavigate();

  const timeoutRef = useRef(null);
  const retryingRef = useRef(false);
  const successTimerRef = useRef(null);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['kiosk-authorization', patientId],
    queryFn: () =>
      getKioskAuthorization(navigate, {
        patientId,
        balance: inRn(values?.amount),
        token: values?.invoiceToken || token,
        processor: terminalData?.cardPresentTerminal?.processor
      }),
    enabled: isOpen,
    refetchOnMount: 'always',
    staleTime: 0
  });

  // Keep the latest onSuccess without re-creating the socket handler each render.
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  const clearTimeoutHandler = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Brief "approved" animation before handing off.
  const finishSuccess = useCallback(
    (transactionId) => {
      clearTimeoutHandler();
      setCardMessage('');
      setStatus('success');
      successTimerRef.current = setTimeout(() => onSuccessRef.current(transactionId), 1700);
    },
    [clearTimeoutHandler]
  );

  const armReconcileTimeout = useCallback(() => {
    clearTimeoutHandler();

    // Safety net: if the live success/error push never lands, reconcile against
    // the actual transaction so a completed charge still resolves the modal
    // instead of hanging on the last card message.
    timeoutRef.current = setTimeout(
      async () => {
        const stopData = await kioskPaymentStop(navigate, {});
        if (stopData?.transaction?.status) {
          if (PAID_TRANSACTION_STATUSES.includes(stopData?.transaction?.status)) {
            finishSuccess(stopData?.transaction?.id);
          } else if (stopData?.message?.includes('TransactionCardReaderNoneAvailable')) {
            setPaymentKind('credit-card');
          } else {
            setStatus('error');
            setErrorMessage(
              toDisplayPaymentMessage(
                stopData?.message,
                "We couldn't finish this payment. Please ask us for help."
              )
            );
          }
        } else {
          setStatus('error');
          setCardMessage('Terminal not connected');
          showNotification({
            icon: 'new-info',
            iconColor: 'warning',
            title: 'Terminal not connected'
          });
        }
      },
      autoStarted ? AUTO_STARTED_RECONCILE_AFTER_MS : RECONCILE_AFTER_MS
    );
  }, [autoStarted, clearTimeoutHandler, finishSuccess, navigate, setErrorMessage]);

  const closeModal = async () => {
    // Ignore close requests once approved so the success handoff isn't dropped.
    if (status === 'success' || isClosing) return;

    setIsClosing(true);

    // Cancelling here (Cancel button, X, or backdrop click) must tell the kiosk
    // app to cancel the in-flight terminal transaction — otherwise the reader is
    // left mid-charge. Reuse the same stop call as the safety-net timeout and
    // reconcile: if the charge actually completed in the race window, resolve as
    // success instead of dropping a real payment.
    try {
      const stopData = await kioskPaymentStop(navigate, {});
      if (PAID_TRANSACTION_STATUSES.includes(stopData?.transaction?.status)) {
        finishSuccess(stopData?.transaction?.id);
        return;
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsClosing(false);
    }

    handleClose();
    setPaymentKind('saved-cards');
    setStatus('initiating');
    setCardMessage('');
    setErrorMessage(null);
    clearTimeoutHandler();
  };

  const handleRetry = async () => {
    // State can't gate concurrent taps — the flag has to flip in the same tick as the check.
    if (retryingRef.current) return;
    retryingRef.current = true;

    clearTimeoutHandler();
    setIsRetrying(true);
    setCardMessage('');
    setErrorMessage(null);
    setStatus('initiating');

    try {
      // The reader may have approved the sale before the error surfaced; re-arming without
      // reconciling first charges the patient twice.
      const stopData = await kioskPaymentStop(navigate, {});
      if (PAID_TRANSACTION_STATUSES.includes(stopData?.transaction?.status)) {
        finishSuccess(stopData?.transaction?.id);
        return;
      }

      // Manual card entry has no terminal sale to re-arm — retrying only clears the error.
      if (paymentKind === 'credit-card') return;

      const { data: retried } = await refetch();

      if (!retried || retried.code !== 0) {
        setStatus('error');
        return;
      }

      if (retried.readerStatus === 'unavailable') {
        setPaymentKind('credit-card');
        return;
      }

      armReconcileTimeout();
    } catch (e) {
      console.error(e);
      setStatus('error');
    } finally {
      retryingRef.current = false;
      setIsRetrying(false);
    }
  };

  const goBack = () =>
    paymentKind === 'credit-card' ? setPaymentKind('saved-cards') : closeModal();

  const handleKioskMessage = useCallback(
    (msgData) => {
      switch (msgData?.kind) {
        case 'handlePaymentConfirmationReceived':
          // The reader only accepted the sale job — no card prompt yet, so the phase is
          // unchanged and the reconciliation timeout stays armed (`success` can be missed).
          break;
        case 'switchToAlternative':
          setPaymentKind('credit-card');
          break;
        case 'success':
          finishSuccess(msgData?.transactionId);
          break;
        case 'readerWaking':
          // The reader has not taken the sale yet, so the phase stays `initiating` — only the
          // caption changes, and the reconciliation timeout stays armed.
          if (typeof msgData?.message === 'string') setCardMessage(msgData.message);
          break;
        case 'handleCardMessageUpdate':
          setCardMessage(snakeToTitleCase(msgData?.message.toLowerCase()));
          setStatus((prev) => {
            const next = classifyReaderStatus(msgData?.message);
            // An approved payment can never be downgraded, and an AUTHORISING prompt that
            // lands before any card prompt is reader setup noise, not real authorization.
            if (prev === 'success') return prev;
            if (prev === 'initiating' && next === 'authorizing') return prev;
            return next;
          });
          break;
        case 'canceled':
          clearTimeoutHandler();
          setStatus('canceled');
          setCardMessage('');
          break;
        case 'error':
          if (msgData?.code === 0) {
            setPaymentKind('credit-card');
          } else if (msgData?.invoiceId) {
            // No invoice means no sale to fail — idle readers raise one per terminal-info ping.
            const errorMessage = toDisplayPaymentMessage(
              msgData?.message,
              'Terminal not connected'
            );
            setStatus('error');
            setCardMessage(errorMessage);
            showNotification({
              icon: 'new-info',
              iconColor: 'warning',
              title: errorMessage
            });
          }
          break;
        default:
          if (msgData?.transactionId && !msgData?.message) finishSuccess(msgData?.transactionId);
          break;
      }
    },
    [finishSuccess, clearTimeoutHandler]
  );

  // A non-zero code means authorization failed server-side and no sale was ever handed to
  // the reader; arming the socket + safety net anyway strands the modal until the timeout
  // reconciles against an unrelated transaction and reports a payment that never happened.
  const authFailed = !!data && data.code !== 0;

  // The reader is down or the practice has no terminal, so authorization never handed a sale to a
  // device: waiting on a card prompt that can't arrive only burns the window before erroring.
  const readerUnavailable = !isFetching && data?.readerStatus === 'unavailable';

  useEffect(() => {
    if (isOpen && authFailed) setStatus('error');
  }, [isOpen, authFailed]);

  useEffect(() => {
    const kAuth = data?.kioskAuthorization;

    try {
      if (!isOpen || authFailed || readerUnavailable || !kAuth || kAuth === 'not_configured')
        return undefined;

      socketOn('kioskPaymentScreenMessage', handleKioskMessage);
      socketEmit('kioskJoinClient', { kioskAuthorization: kAuth }, { reemitOnReconnect: true });

      armReconcileTimeout();
    } catch (e) {
      console.error(e);
    }

    return () => {
      socketOff('kioskPaymentScreenMessage', handleKioskMessage);
      clearTimeoutHandler();
    };
  }, [
    isOpen,
    authFailed,
    readerUnavailable,
    data?.kioskAuthorization,
    handleKioskMessage,
    armReconcileTimeout,
    clearTimeoutHandler
  ]);

  useEffect(() => {
    if (!socket.connected) {
      socket.connect();
    }
  }, [socket.connected]);

  useEffect(() => {
    if (isOpen) {
      setStatus('initiating');
      setCardMessage('');
      setPaymentKind('saved-cards');
      setIsClosing(false);
      setIsRetrying(false);
      retryingRef.current = false;
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && readerUnavailable) setPaymentKind('credit-card');
  }, [isOpen, readerUnavailable]);

  useEffect(() => () => clearTimeout(successTimerRef.current), []);

  const hideCardOptions =
    paymentKind === 'saved-cards' && (status === 'authorizing' || status === 'success');

  return (
    <>
      <Modal
        isOpen={isOpen}
        handleClose={closeModal}
        width="full"
        title={<Header goBack={goBack} step={paymentKind} isDisabled={isClosing} />}
        footer={
          <Footer
            step={paymentKind}
            amount={values.amount}
            status={status}
            onClose={goBack}
            onRetry={handleRetry}
            isClosing={isClosing}
            isRetrying={isRetrying}
          />
        }>
        <div className="flex min-h-full flex-col">
          <div
            className="my-auto flex w-full max-w-[600px] flex-col items-center gap-10 self-center px-6 pb-10 pt-8 xs:gap-8 xs:px-4"
            ref={scrollRef}>
            <Information
              paymentKind={paymentKind}
              cardMessage={cardMessage}
              amount={mString(values?.amount)}
              status={status}
              showManualEntry={!!terminalData?.onlineTerminal}
            />
            {isLoading ? (
              <Loader color="primary" size={56} />
            ) : hideCardOptions ? null : (
              <CardContent
                ref={scrollRef}
                active={paymentKind}
                setActive={setPaymentKind}
                setErrorMessage={setErrorMessage}
                patientId={patientId}
              />
            )}
          </div>
        </div>
      </Modal>
      <ErrorPaymentModal
        isOpen={!!error}
        message={error}
        onClearError={() => setErrorMessage(null)}
        onClose={closeModal}
        onTryAgain={handleRetry}
      />
    </>
  );
};

export default PayFromKioskModal;
